import { useTranslation } from 'react-i18next';
import { X, CheckCircle2, Circle, ClipboardCheck } from 'lucide-react';
import { useHandoverItems } from '../hooks/useHandovers';

/**
 * Modal showing one handover report: meta info, structured items
 * (tickable by the incoming user), acknowledge action.
 *
 * @param {Object} props
 * @param {Object} props.handover - shift_handovers row.
 * @param {Object} props.user - Current user (needs id).
 * @param {Object} props.hook - Value of useHandovers(user).
 * @param {Function} props.onClose - Called when the modal should close.
 */
export function HandoverDetail({ handover, user, hook, onClose }) {
    const { t } = useTranslation();
    const { items } = useHandoverItems(handover?.id ?? null);

    const isIncoming = handover?.incoming_user_id === user?.id;
    const canTick = isIncoming && ['submitted', 'acknowledged'].includes(handover?.status);
    const canAcknowledge = isIncoming && handover?.status === 'submitted';
    // Creator-completion: the creator may complete their own handover when no
    // successor was named (RLS path added in migration 20260920120000).
    const isCreator = handover?.outgoing_user_id === user?.id;
    const canCreatorComplete = isCreator && !handover?.incoming_user_id && handover?.status === 'submitted';

    const statusBadge = {
        draft: 'hub-badge hub-badge-muted',
        submitted: 'hub-badge hub-badge-warn',
        acknowledged: 'hub-badge hub-badge-succ',
    }[handover?.status] ?? 'hub-badge hub-badge-muted';

    const itemBadge = {
        info: 'hub-badge hub-badge-info',
        task: 'hub-badge hub-badge-warn',
        incident: 'hub-badge hub-badge-err',
    };

    return (
        <div
            className="fixed inset-0 z-60 bg-black/40 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-surface w-full max-w-2xl max-h-[90vh] rounded-4xl border border-border shadow-2xl flex flex-col animate-in slide-in-from-bottom-4"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0 gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <h3 className="font-black text-fg text-sm uppercase tracking-widest">
                            {t('handover.detail.title')}
                        </h3>
                        <span className="text-xs font-bold text-muted">
                            {handover?.handover_date} · {t(`handover.shift.${handover?.shift_label}`)}
                        </span>
                        <span className={`${statusBadge} text-[9px]`}>
                            {t(`handover.status.${handover?.status}`)}
                        </span>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-bg text-muted shrink-0">
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5 min-h-0">
                    {/* General notes */}
                    {handover?.general_notes && (
                        <div className="border border-border rounded-2xl p-4 bg-bg/50">
                            <p className="text-sm text-fg whitespace-pre-wrap">{handover.general_notes}</p>
                        </div>
                    )}

                    {/* Items checklist */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <ClipboardCheck size={14} className="text-accent" />
                            <span className="text-[10px] font-black uppercase tracking-widest text-muted">
                                {t('handover.detail.items', { count: items.length })}
                            </span>
                        </div>
                        {items.map(item => (
                            <div
                                key={item.id}
                                className={`flex items-start gap-3 border border-border rounded-xl px-3 py-2.5 ${item.is_done ? 'opacity-60' : ''}`}
                            >
                                {canTick ? (
                                    <button
                                        onClick={() => hook.tickItem(handover.id, item.id, !item.is_done)}
                                        className="shrink-0 mt-0.5 text-accent"
                                        aria-label={t('handover.detail.toggleDone')}
                                    >
                                        {item.is_done ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                                    </button>
                                ) : (
                                    <span className="shrink-0 mt-0.5 text-muted">
                                        {item.is_done ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                                    </span>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`${itemBadge[item.item_type] ?? 'hub-badge hub-badge-muted'} text-[9px]`}>
                                            {t(`handover.itemTypes.${item.item_type}`)}
                                        </span>
                                        {item.room_ref && (
                                            <span className="text-[9px] font-bold text-muted uppercase">
                                                {t('handover.detail.room', { room: item.room_ref })}
                                            </span>
                                        )}
                                    </div>
                                    <p className={`text-sm font-medium text-fg ${item.is_done ? 'line-through' : ''}`}>
                                        {item.content}
                                    </p>
                                </div>
                            </div>
                        ))}
                        {items.length === 0 && (
                            <p className="text-xs text-muted">{t('handover.detail.noItems')}</p>
                        )}
                    </div>
                </div>

                {/* Footer: acknowledge */}
                <div className="px-6 py-4 border-t border-border shrink-0 flex items-center gap-3">
                    {handover?.acknowledged_at && (
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">
                            {t('handover.detail.acknowledgedAt', { time: new Date(handover.acknowledged_at).toLocaleString() })}
                        </span>
                    )}
                    {canCreatorComplete && (
                        <button
                            onClick={() => hook.acknowledgeHandover(handover.id).then(onClose)}
                            className="ml-auto flex items-center gap-2 py-3 px-6 rounded-xl text-xs font-black uppercase tracking-widest bg-accent text-white hover:opacity-90"
                        >
                            <CheckCircle2 size={14} />
                            {t('handover.detail.completeMine')}
                        </button>
                    )}
                    {canAcknowledge && !canCreatorComplete && (
                        <button
                            onClick={() => hook.acknowledgeHandover(handover.id).then(onClose)}
                            className="ml-auto flex items-center gap-2 py-3 px-6 rounded-xl text-xs font-black uppercase tracking-widest bg-accent text-white hover:opacity-90"
                        >
                            <CheckCircle2 size={14} />
                            {t('handover.detail.acknowledge')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
